import React, { useState } from 'react';
import {
    Building2, Users, TrendingUp, Calendar, Bell, FileText,
    Plus, Edit2, Trash2, Check, Phone, Mail,
    DollarSign, Clock, Archive, UserPlus, ArrowRight
} from 'lucide-react';
import { isEntityActive, StatusFilter } from '../helpers';

function DashboardView({ stats, upcomingReminders, onCompleteReminder, onNavigate }) {
  if (!stats) return null;

  const overdueCount = stats.reminders.overdue || 0;
  const todayCount = stats.reminders.today || 0;
  const reminderParts = [];
  if (overdueCount > 0) reminderParts.push(`${overdueCount} overdue`);
  if (todayCount > 0) reminderParts.push(`${todayCount} due today`);
  const reminderSub = reminderParts.length > 0 ? reminderParts.join(' · ') : 'All caught up';

  const pipelineRaw = stats.leads.pipelineValue || 0;
  const pipelineFormatted = pipelineRaw >= 1000000
    ? `$${(pipelineRaw / 1000000).toFixed(1)}M`
    : pipelineRaw >= 1000
      ? `$${(pipelineRaw / 1000).toFixed(0)}K`
      : `$${pipelineRaw.toLocaleString()}`;

  return (
    <div>
      {/* Stats Grid */}
      <div className="crm-grid crm-grid--4 crm-mb-2">
        <StatsCard
          icon={Users}
          label="Active Contacts"
          value={stats.contacts.total}
          color="var(--primary)"
          linkText="View contacts"
          onClick={() => onNavigate && onNavigate('contacts')}
        />
        <StatsCard
          icon={TrendingUp}
          label="Active Leads"
          value={stats.leads.total}
          color="var(--info)"
          subValue={`${stats.leads.byStatus?.length || 0} statuses in pipeline`}
          linkText="View leads"
          onClick={() => onNavigate && onNavigate('leads')}
        />
        <StatsCard
          icon={Bell}
          label="Pending Reminders"
          value={stats.reminders.total}
          color={overdueCount > 0 ? 'var(--warning)' : 'var(--success)'}
          subValue={reminderSub}
          subValueAlert={overdueCount > 0}
          linkText="View reminders"
          onClick={() => onNavigate && onNavigate('reminders')}
        />
        <StatsCard
          icon={DollarSign}
          label="Pipeline Value"
          value={pipelineFormatted}
          color="var(--purple)"
          subValue={`Across ${stats.leads.total} active leads`}
          linkText="View leads"
          onClick={() => onNavigate && onNavigate('leads')}
        />
      </div>

      {/* Upcoming Reminders */}
      <div className="crm-card">
        <div className="crm-card__header">
          <h2 className="crm-card__title">
            <Calendar size={24} className="crm-card__icon" />
            Upcoming Reminders
          </h2>
        </div>
        
        {upcomingReminders.length === 0 ? (
          <p className="crm-text--muted crm-text--center" style={{ padding: '2rem' }}>
            No upcoming reminders
          </p>
        ) : (
          <div className="crm-reminder-list">
            {upcomingReminders.slice(0, 5).map(reminder => (
              <ReminderCard key={reminder.ReminderId} reminder={reminder} onComplete={onCompleteReminder} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Stats Card Component
function StatsCard({ icon: Icon, label, value, color, subValue, subValueAlert, linkText, onClick }) {
  return (
    <div className="crm-stat-card" style={{ '--card-accent': color }} onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}>
      <div className="crm-stat-card__header">
        <div className="crm-stat-card__icon-wrapper">
          <Icon size={20} />
        </div>
        <span className="crm-stat-card__label">{label}</span>
      </div>
      <div className="crm-stat-card__value">{value}</div>
      {subValue && <div className={`crm-stat-card__subvalue${subValueAlert ? ' crm-stat-card__subvalue--alert' : ''}`}>{subValue}</div>}
      {linkText && (
        <div className="crm-stat-card__link">
          {linkText} <ArrowRight size={14} />
        </div>
      )}
    </div>
  );
}

// Reminder Card Component
function ReminderCard({ reminder, onComplete }) {
  const priorityColors = {
    Urgent: '#dc2626',
    High: '#f59e0b',
    Medium: '#2563eb',
    Low: '#78716c'
  };

  return (
    <div className="crm-reminder-card" style={{ border: `1px solid ${priorityColors[reminder.Priority]}30` }}>
      <div className="crm-reminder-card__content">
        <div className="crm-reminder-card__header">
          <span style={{
            background: `${priorityColors[reminder.Priority]}15`,
            color: priorityColors[reminder.Priority],
            padding: '0.25rem 0.625rem',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            {reminder.Priority}
          </span>
          <span className="crm-reminder-card__title">{reminder.Title}</span>
        </div>
        <div className="crm-reminder-card__meta">
          <Clock size={14} className="crm-reminder-card__meta-icon" />
          {new Date(reminder.DueDate).toLocaleDateString()}
          {reminder.RelatedToName && ` • ${reminder.RelatedToName}`}
        </div>
      </div>
      <button
        onClick={() => onComplete(reminder.ReminderId)}
        className="crm-btn crm-btn--success"
      >
        <Check size={16} />
        Complete
      </button>
    </div>
  );
}

// Contacts View Component
function ContactsView({ contacts, searchTerm, onSearch, onAdd, onEdit, onDelete, onArchive, onViewDetail }) {
  const [statusFilter, setStatusFilter] = useState('Active');

  const filteredContacts = contacts.filter(c => {
    // Status filter
    if (statusFilter === 'Active' && !isEntityActive('contact', c)) return false;
    if (statusFilter === 'Inactive' && isEntityActive('contact', c)) return false;
    // Search filter
    if (!searchTerm) return true;
    return c.FirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.LastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.CompanyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.Email?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div>
      <div className="crm-page-header">
        <h2 className="crm-page-title">Contacts</h2>
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
      </div>
      <div className="crm-toolbar">
        <button onClick={onAdd} className="crm-btn crm-btn--primary crm-btn--add">
          <Plus size={18} />
          Add Contact
        </button>
      </div>

      <div className="crm-grid crm-grid--2">
        {filteredContacts.map(contact => (
            <ContactCard key={contact.ContactId} contact={contact} onEdit={onEdit} onDelete={onDelete} onArchive={onArchive} onViewDetail={onViewDetail} />
        ))}
      </div>

      {filteredContacts.length === 0 && (
        <div className="crm-empty-state">
          <Users size={48} className="crm-empty-state__icon" />
          <p className="crm-empty-state__text">No contacts found</p>
        </div>
      )}
    </div>
  );
}

// Contact Card Component
function ContactCard({ contact, onEdit, onDelete, onArchive, onViewDetail }) {
  return (
    <div className="crm-card" onClick={() => onViewDetail(contact)} style={{ cursor: 'pointer' }}>
      <div className="crm-contact-card">
        <div className="crm-contact-card__avatar">
          {contact.FirstName?.[0]}{contact.LastName?.[0]}
        </div>
        <div className="crm-contact-card__content">
          <div className="crm-contact-card__header">
            <div>
              <h3 className="crm-contact-card__name">
                {contact.FirstName} {contact.LastName}
              </h3>
              {contact.CompanyName && (
                <p className="crm-contact-card__company">
                  {contact.CompanyName}
                </p>
              )}
            </div>
            <span className="crm-badge crm-badge--taupe">{contact.ContactType}</span>
          </div>
          
          <div className="crm-contact-card__info">
            {contact.Email && (
              <div className="crm-contact-card__info-item">
                <Mail size={14} className="crm-contact-card__info-icon" />
                {contact.Email}
              </div>
            )}
            {contact.Phone && (
              <div className="crm-contact-card__info-item">
                <Phone size={14} className="crm-contact-card__info-icon" />
                {contact.Phone}
              </div>
            )}
          </div>

          <div className="crm-contact-card__footer" onClick={(e) => e.stopPropagation()}>
            <div className="crm-contact-card__meta">
              <Building2 size={14} className="crm-contact-card__meta-icon" />
              {contact.PropertyCount || 0} {contact.PropertyCount === 1 ? 'property' : 'properties'}
            </div>
            <button onClick={() => onEdit(contact)} className="crm-btn crm-btn--secondary">
              <Edit2 size={14} />
              Edit
            </button>
            <button onClick={() => onArchive(contact.ContactId)} className="crm-btn crm-btn--secondary" data-tooltip="Archive">
              <Archive size={14} />
            </button>
            <button onClick={() => onDelete(contact.ContactId)} className="crm-btn crm-btn--danger" data-tooltip="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Leads View Component
function LeadsView({ leads, searchTerm, onSearch, onAdd, onEdit, onDelete, onArchive, onViewDetail, onConvert }) {
  const [statusFilter, setStatusFilter] = useState('Active');

  const filteredLeads = leads.filter(l => {
    if (statusFilter === 'Active' && !isEntityActive('lead', l)) return false;
    if (statusFilter === 'Inactive' && isEntityActive('lead', l)) return false;
    if (!searchTerm) return true;
    return (l.ContactFirstName || l.FirstName)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (l.ContactLastName || l.LastName)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (l.ContactCompany || l.CompanyName)?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const statusColors = {
    'New': '#2563eb',
    'Contacted': '#f59e0b',
    'Qualified': '#7c3aed',
    'Negotiating': '#e11d48',
    'Closed-Won': '#10b981',
    'Closed-Lost': '#78716c'
  };

  return (
    <div>
      <div className="crm-page-header">
        <h2 className="crm-page-title">Leads</h2>
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
      </div>
      <div className="crm-toolbar">
        <button onClick={onAdd} className="crm-btn crm-btn--primary crm-btn--add">
          <Plus size={18} />
          Add Lead
        </button>
      </div>

      <div className="crm-reminder-list-full">
        {filteredLeads.map(lead => (
          <div key={lead.LeadId} className="crm-card" onClick={() => onViewDetail(lead)} style={{ cursor: 'pointer' }}>
            <div className="crm-lead-card">
              <div className="crm-lead-card__content">
                <div className="crm-lead-card__header">
                  <h3 className="crm-lead-card__name">
                    {lead.ContactFirstName || lead.FirstName} {lead.ContactLastName || lead.LastName}
                  </h3>
                  <span style={{
                    background: `${statusColors[lead.Status]}15`,
                    color: statusColors[lead.Status],
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {lead.Status}
                  </span>
                </div>
                
                {(lead.ContactCompany || lead.CompanyName) && (
                  <p className="crm-lead-card__company">
                    {lead.ContactCompany || lead.CompanyName}
                  </p>
                )}
                
                {lead.PropertyInterest && (
                  <p className="crm-lead-card__interest">
                    <Building2 size={14} className="crm-lead-card__interest-icon" />
                    {lead.PropertyInterest}
                  </p>
                )}

                <div className="crm-lead-card__meta">
                  {lead.EstimatedValue && (
                    <div className="crm-lead-card__meta-item">
                      <span className="crm-lead-card__meta-label">Value:</span>
                      <span className="crm-lead-card__meta-value crm-lead-card__meta-value--success">
                        ${(lead.EstimatedValue / 1000).toFixed(0)}K
                      </span>
                    </div>
                  )}
                  {lead.NextFollowUpDate && (
                    <div className="crm-lead-card__meta-item">
                      <span className="crm-lead-card__meta-label">Follow-up:</span>
                      <span className="crm-lead-card__meta-value">
                        {new Date(lead.NextFollowUpDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="crm-lead-card__actions" onClick={(e) => e.stopPropagation()}>
                        {lead.Status !== 'Closed-Won' && lead.Status !== 'Closed-Lost' && (
                                <button onClick={() => onConvert(lead)} className="crm-btn crm-btn--success" data-tooltip="Convert to Contract">
                                      <UserPlus size={14} />
                                    </button>
                              )}
               <button onClick={() => onEdit(lead)} className="crm-btn crm-btn--secondary">
                  <Edit2 size={14} />
                  Edit
                </button>
                <button onClick={() => onArchive(lead.LeadId)} className="crm-btn crm-btn--secondary" data-tooltip="Archive">
                  <Archive size={14} />
                </button>
                <button onClick={() => onDelete(lead.LeadId)} className="crm-btn crm-btn--danger" data-tooltip="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredLeads.length === 0 && (
        <div className="crm-empty-state">
          <TrendingUp size={48} className="crm-empty-state__icon" />
          <p className="crm-empty-state__text">No leads found</p>
        </div>
      )}
    </div>
  );
}

// Reminders View Component
function RemindersView({ reminders, searchTerm, onSearch, onAdd, onEdit, onDelete, onComplete, onArchive }) {
  const [statusFilter, setStatusFilter] = useState('Active');

  const filtered = reminders.filter(r => {
    if (statusFilter === 'Active' && !isEntityActive('reminder', r)) return false;
    if (statusFilter === 'Inactive' && isEntityActive('reminder', r)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (r.Title || '').toLowerCase().includes(term) ||
      (r.Description || '').toLowerCase().includes(term) ||
      (r.RelatedToName || '').toLowerCase().includes(term) ||
      (r.ReminderType || '').toLowerCase().includes(term);
  }).sort((a, b) => new Date(b.DueDate) - new Date(a.DueDate));

  return (
    <div>
      <div className="crm-page-header">
        <h2 className="crm-page-title">Reminders</h2>
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
      </div>
      <div className="crm-toolbar">
        <button onClick={onAdd} className="crm-btn crm-btn--primary crm-btn--add">
          <Plus size={18} />
          Add Reminder
        </button>
      </div>

      <div className="crm-reminder-list-full">
        {filtered.map(reminder => (
          <div key={reminder.ReminderId} className="crm-card" style={reminder.Status === 'Completed' ? { opacity: 0.6 } : {}}>
            <div className="crm-reminder-full-card">
              <div className="crm-reminder-full-card__content">
                <div className="crm-reminder-full-card__header">
                  {reminder.Status === 'Completed' ? (
                    <span className="crm-badge crm-badge--green">Done</span>
                  ) : (
                    <span className={`crm-badge crm-badge--${reminder.Priority === 'High' || reminder.Priority === 'Urgent' ? 'red' : reminder.Priority === 'Medium' ? 'yellow' : 'blue'}`}>
                      {reminder.Priority}
                    </span>
                  )}
                  <span className="crm-badge crm-badge--purple">{reminder.ReminderType}</span>
                  <h3 className="crm-reminder-full-card__title">{reminder.Title}</h3>
                </div>
                <div className="crm-reminder-full-card__meta">
                  <Clock size={14} className="crm-reminder-full-card__meta-icon" />
                  Due: {new Date(reminder.DueDate).toLocaleDateString()}
                  {reminder.DueTime && ` at ${new Date('1970-01-01T' + reminder.DueTime.split('T').pop()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                  {reminder.RelatedToName && ` • ${reminder.RelatedToName}`}
                </div>
              </div>
              <div className="crm-reminder-full-card__actions">
                        {reminder.Status !== 'Completed' && (
                        <button onClick={() => onComplete(reminder.ReminderId)} className="crm-btn crm-btn--success" data-tooltip="Complete">
                  <Check size={14} />
                </button>
                        )}
                        <button onClick={() => onEdit(reminder)} className="crm-btn crm-btn--secondary" data-tooltip="Edit">
                  <Edit2 size={14} />
                </button>
                        <button onClick={() => onArchive(reminder.ReminderId)} className="crm-btn crm-btn--secondary" data-tooltip="Archive">
                  <Archive size={14} />
                </button>
                        <button onClick={() => onDelete(reminder.ReminderId)} className="crm-btn crm-btn--danger" data-tooltip="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {reminders.length === 0 && (
        <div className="crm-empty-state">
          <Bell size={48} className="crm-empty-state__icon" />
          <p className="crm-empty-state__text">No pending reminders</p>
        </div>
      )}
    </div>
  );
}

// Contracts View Component
function ContractsView({ contracts, searchTerm, onSearch, onAdd, onEdit, onDelete, onArchive, onViewDetail }) {
  const [statusFilter, setStatusFilter] = useState('Active');

  const filtered = contracts.filter(c => {
    if (statusFilter === 'Active' && !isEntityActive('contract', c)) return false;
    if (statusFilter === 'Inactive' && isEntityActive('contract', c)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (c.ContractNumber || '').toLowerCase().includes(term) ||
      (c.ContactName || '').toLowerCase().includes(term) ||
      (c.ContractType || '').toLowerCase().includes(term) ||
      (c.Status || '').toLowerCase().includes(term) ||
      (c.PropertyAddress || '').toLowerCase().includes(term);
  });

  return (
    <div>
      <div className="crm-page-header">
        <h2 className="crm-page-title">Contracts</h2>
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
      </div>
      <div className="crm-toolbar">
        <button onClick={onAdd} className="crm-btn crm-btn--primary crm-btn--add">
          <Plus size={18} />
          Add Contract
        </button>
      </div>

      <div className="crm-grid crm-grid--2">
        {filtered.map(contract => (
          <div key={contract.ContractId} className="crm-card" onClick={() => onViewDetail(contract)} style={{ cursor: 'pointer' }}>
            <div className="crm-contract-card">
              <div className="crm-contract-card__content">
                <div className="crm-contract-card__header">
                  <span className={`crm-badge crm-badge--${
                    contract.Status === 'Active' ? 'green' :
                    contract.Status === 'Signed' ? 'blue' :
                    contract.Status === 'Draft' ? 'yellow' : 'blue'
                  }`}>
                    {contract.Status}
                  </span>
                  <h3 className="crm-contract-card__title">{contract.ContractNumber}</h3>
                  <span className="crm-contract-card__type">{contract.ContractType}</span>
                </div>
                <div className="crm-contract-card__meta">
                  {contract.ContactName}
                  {contract.ContactCompany && ` • ${contract.ContactCompany}`}
                  {contract.Value != null && ` • $${(contract.Value / 1000).toFixed(0)}K`}
                  {contract.StartDate && ` • ${new Date(contract.StartDate).toLocaleDateString()}`}
                  {contract.EndDate && ` - ${new Date(contract.EndDate).toLocaleDateString()}`}
                </div>
              </div>
              <div className="crm-contract-card__actions" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => onEdit(contract)} className="crm-btn crm-btn--secondary" data-tooltip="Edit">
                  <Edit2 size={14} />
                </button>
                <button onClick={() => onArchive(contract.ContractId)} className="crm-btn crm-btn--secondary" data-tooltip="Archive">
                  <Archive size={14} />
                </button>
                <button onClick={() => onDelete(contract.ContractId)} className="crm-btn crm-btn--danger" data-tooltip="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {contracts.length === 0 && (
        <div className="crm-empty-state">
          <FileText size={48} className="crm-empty-state__icon" />
          <p className="crm-empty-state__text">No contracts found</p>
        </div>
      )}
    </div>
  );
}

// Properties View Component
function PropertiesView({ properties, searchTerm, onSearch, onAdd, onEdit, onDelete, onArchive, onViewDetail }) {
  const [statusFilter, setStatusFilter] = useState('Active');

  const filtered = properties.filter(p => {
    if (statusFilter === 'Active' && !isEntityActive('property', p)) return false;
    if (statusFilter === 'Inactive' && isEntityActive('property', p)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (p.PropertyName || '').toLowerCase().includes(term) ||
      (p.PropertyAddress || '').toLowerCase().includes(term) ||
      (p.City || '').toLowerCase().includes(term) ||
      (p.OwnerName || '').toLowerCase().includes(term) ||
      (p.OwnerCompany || '').toLowerCase().includes(term)
    );
  });

  return (
    <div>
      <div className="crm-page-header">
        <h2 className="crm-page-title">Properties</h2>
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
      </div>
      <div className="crm-toolbar">
        <button onClick={onAdd} className="crm-btn crm-btn--primary crm-btn--add">
          <Plus size={18} />
          Add Property
        </button>
      </div>

      {filtered.length > 0 ? (
        <div className="crm-grid crm-grid--2">
          {filtered.map(property => (
            <div key={property.PropertyId} className="crm-card" onClick={() => onViewDetail(property)} style={{ cursor: 'pointer' }}>
              <div className="crm-card__body">
                <div className="crm-contact-card__header">
                  <div>
                    <h3 className="crm-contact-card__name">
                      {property.PropertyName}
                      {property.YardiCode && (
                        <span style={{ fontSize: '0.7rem', fontFamily: 'monospace', color: 'var(--text-muted)', marginLeft: '8px', fontWeight: '400' }}>{property.YardiCode}</span>
                      )}
                    </h3>
                    {property.PropertyAddress && (
                      <p className="crm-contact-card__company">
                        {property.PropertyAddress}
                        {property.City && `, ${property.City}`}
                        {property.State && `, ${property.State}`}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {property.PropertyType && (
                      <span className="crm-badge crm-badge--blue">{property.PropertyType}</span>
                    )}
                    {property.Status && (
                      <span className={`crm-badge ${property.Status === 'Active' ? 'crm-badge--green' : property.Status === 'Vacant' ? 'crm-badge--yellow' : 'crm-badge--blue'}`}>
                        {property.Status}
                      </span>
                    )}
                  </div>
                </div>

                <div className="crm-contact-card__info">
                  {property.OwnerName && (
                    <div className="crm-contact-card__info-item">
                      <Users size={14} className="crm-contact-card__info-icon" />
                      {property.OwnerName}{property.OwnerCompany ? ` — ${property.OwnerCompany}` : ''}
                    </div>
                  )}
                  {property.Units && (
                    <div className="crm-contact-card__info-item">
                      <Building2 size={14} className="crm-contact-card__info-icon" />
                      {property.Units} Units
                    </div>
                  )}
                  {property.MarketValue && (
                    <div className="crm-contact-card__info-item">
                      <DollarSign size={14} className="crm-contact-card__info-icon" />
                      ${Number(property.MarketValue).toLocaleString()}
                    </div>
                  )}
                </div>

                <div className="crm-contact-card__footer" onClick={(e) => e.stopPropagation()}>
                  <div className="crm-contact-card__meta">
                    {property.MonthlyRevenue && (
                      <span style={{ color: 'var(--success)', fontWeight: '600', fontSize: '0.75rem' }}>
                        ${Number(property.MonthlyRevenue).toLocaleString()}/mo
                      </span>
                    )}
                  </div>
                  <div className="crm-contact-card__actions">
                    <button onClick={() => onEdit(property)} className="crm-btn crm-btn--secondary">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => onArchive(property.PropertyId)} className="crm-btn crm-btn--secondary" data-tooltip="Archive">
                      <Archive size={14} />
                    </button>
                    <button onClick={() => onDelete(property.PropertyId)} className="crm-btn crm-btn--danger">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="crm-empty-state">
          <Building2 size={48} className="crm-empty-state__icon" />
          <p className="crm-empty-state__text">No properties found</p>
        </div>
      )}
    </div>
  );
}

// Lead Property Selector — fetches properties for the selected contact

export { DashboardView, ContactsView, LeadsView, RemindersView, ContractsView, PropertiesView };
